import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import CMSConsumerCarouselBanner from '../CMS/components/banner/CMSConsumerCarouselBanner';
import TextHeader from './components/TextHeader';
import FeaturedGallery from '../../components/shared/gallery/FeaturedGallery';
import CatalogueGrid from './components/CatalogueGrid';
import ImageCards from './components/ImageCards';
import { Box, Alert, CircularProgress, Typography } from '@mui/material';
import { captureAnalytics } from '../../utilities/Analytics';
import YouTubeVideo from './components/YouTubeVideo';

// Import the API (using your combined file)
import {
  apiGetBrandDisplay,
  apiGetInternationalsDisplay,
  BrandPageData,
  InternationalBrandPage as InternationalBrandPageType // ← Rename the type
} from '../../api/internationalsApiDisplay';

// Define the params type for useParams
interface BrandParams {
  brandId: string;
  [key: string]: string | undefined;
}

const InternationalBrandPage = () => {
  const { brandId } = useParams<BrandParams>();
  const location = useLocation();

  // State management
  const [brandData, setBrandData] = useState<BrandPageData | null>(null);
  const [brands, setBrands] = useState<InternationalBrandPageType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Scroll to top when the page loads
    window.scrollTo(0, 0);

    // Load brand data from API
    const loadBrandData = async () => {
      if (!brandId) {
        setError('Brand ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load both the specific brand data AND all brands for navigation
        const [brandResponse, allBrandsResponse] = await Promise.all([
          apiGetBrandDisplay(brandId),
          apiGetInternationalsDisplay()
        ]);

        if (brandResponse.success && brandResponse.brand_page) {
          setBrandData(brandResponse.brand_page);

          // Capture analytics
          captureAnalytics('pageview', location.pathname, `Internationals - ${brandResponse.brand_page.page_title}`);
        } else {
          setError(brandResponse.msg || 'Failed to load brand data');
        }

        // Load all brands for the ImageCards navigation
        if (allBrandsResponse.success && allBrandsResponse.settings?.brands) {
          setBrands(allBrandsResponse.settings.brands);
        }

      } catch (err) {
        console.error('Error loading brand data:', err);
        setError('Failed to load brand data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadBrandData();
  }, [brandId, location.pathname]);

  // Helper functions to transform API data for components - USING CONSUMER DATA
  const getCarouselImages = () => {
    if (!brandData) return [];

    // Use consumer banner for display
    if (brandData.consumer_banner_media_switch === 'carousel' && brandData.consumer_banner_carousel) {
      return brandData.consumer_banner_carousel.map(item => ({
        url: item.img_url || item.video_url || ''
      })).filter(item => item.url !== '');
    }

    return [];
  };

  const getCarouselVideo = () => {
    if (!brandData || brandData.consumer_banner_media_switch !== 'video') return undefined;

    if (brandData.consumer_banner_video_url) {
      return {
        video_url: brandData.consumer_banner_video_url,
        thumbnail_url: ''
      };
    }

    return undefined;
  };

  const getCarouselAspectRatio = () => {
    if (!brandData) return "56.25%";

    const width = brandData.consumer_banner_width || 2800;
    const height = brandData.consumer_banner_height || 1600;
    const aspectRatio = (height / width) * 100;
    return `${aspectRatio}%`;
  };

  const getFeaturedGalleryData = () => {
    if (!brandData?.featured_collection_media) return [];

    return brandData.featured_collection_media.map(media => ({
      position: media.position,
      url: media.img_url || ''
    }));
  };

  const getCatalogueData = (section: 'first' | 'second') => {
    if (!brandData) return [];

    const items = section === 'first'
      ? brandData.catalogue_section_items_first
      : brandData.catalogue_section_items_second;

    return items.map(item => ({
      id: item.id,
      img: item.catalogue_tile_img_url || '',
      url: item.issuu_link
    }));
  };

  const transformBrandPageLink = (brandPageLink: string | null | undefined): string | null | undefined => {
    if (!brandPageLink || typeof brandPageLink !== 'string') {
      return brandPageLink;
    }

    // Check if the URL contains "trade." and remove it
    if (brandPageLink.includes('trade.')) {
      return brandPageLink.replace('trade.', '');
    }

    return brandPageLink;
  };

  const getBrandAssets = () => {
    if (!brands || brands.length === 0) {
      return [];
    }

    const publishedBrands = brands.filter(brand => brand.is_published === true);
    const sortedBrands = publishedBrands.sort((a, b) => a.position_order - b.position_order);

    const brandAssets = sortedBrands.map(brand => {
      // Construct tile image URL from filename if needed
      const imageUrl = brand.tile_img_url ||
        (brand.tile_img ? `https://file-server.hertex-staging.net/media/large/${brand.tile_img}` : '');

      // Transform the brand_page_link to remove "trade." if present
      const transformedBrandPageLink = transformBrandPageLink(brand.brand_page_link);

      console.log(`Brand ${brand.page_title}: imageUrl=${imageUrl}, original_brand_page_link=${brand.brand_page_link}, transformed_brand_page_link=${transformedBrandPageLink}`);

      return {
        img: imageUrl,
        title: brand.page_title,
        url: `/internationals/brand/${brand.id}`,
        slug: brand.id.toString(),
        brand_page_link: transformedBrandPageLink // ✅ Use transformed URL
      };
    });

    return brandAssets;
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress />
        <Box sx={{ fontSize: '18px', color: '#666' }}>
          Loading brand information...
        </Box>
      </Box>
    );
  }

  // Show error state
  if (error || !brandData) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        px: 2
      }}>
        <Alert severity="error" sx={{ maxWidth: '600px' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ fontSize: '18px', fontWeight: 'bold', mb: 1 }}>
              Unable to load brand information
            </Box>
            <Box sx={{ mb: 2 }}>
              {error || 'Brand not found or not published'}
            </Box>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </Box>
        </Alert>
      </Box>
    );
  }

  const carouselImages = getCarouselImages();
  const carouselVideo = getCarouselVideo();
  const carouselAspectRatio = getCarouselAspectRatio();
  const featuredImages = getFeaturedGalleryData();
  const firstCatalogues = getCatalogueData('first');
  const brandAssets = getBrandAssets(); // Now defined!

  return (
    <>

      {brandData && (brandData.consumer_banner_carousel.length > 0 || brandData.consumer_banner_video) && (
        <CMSConsumerCarouselBanner
          bannerMediaOption={brandData.consumer_banner_media_switch}
          bannerMediaCarousel={brandData.consumer_banner_carousel.map(item => ({
            id: item.id,
            media_url: item.img_url || item.video_url || '',
            media_type: item.type,
            media_name: item.img_name || item.video_name || ''
          }))}
          bannerMediaVideo={brandData.consumer_banner_video || null}
          bannerMediaVideoLink={brandData.consumer_banner_video_url || ''}
          bannerLinkCategoryId={null} // No category linking for internationals
          bannerWidth={brandData.consumer_banner_width || 2800}
          bannerHeight={brandData.consumer_banner_height || 1600}
          viewMode="live"
        />
      )}

      {/* Brand Title and Description */}
      <TextHeader
        heading={brandData.page_title}
        content={brandData.page_description}
        isCentered={false}
        width='70%'
        marginTop={10}
        marginBottom={5}
      />

      {/* Featured Gallery - FIXED: Now uses modal instead of external link */}
      {featuredImages.length > 0 && brandData.featured_collection_title && (
        <FeaturedGallery
          images={featuredImages}
          hasVideo={false}
          invert={false}
          title={brandData.featured_collection_title}
          description={brandData.featured_collection_description}
          useModal={true}
          modalUrl={brandData.featured_collection_link || undefined}
        />
      )}

      {/* First Catalogue Section - Using CatalogueGrid */}
      {firstCatalogues.length > 0 && brandData.catalogue_section_heading_first && (
        <CatalogueGrid
          header={brandData.catalogue_section_heading_first}
          catalogueData={firstCatalogues}
        />
      )}

      {/* YouTube Promo Video - Added more spacing */}
      {brandData.promo_video && (
        <Box sx={{
          py: { xs: 8, md: 12 }, // Increased from py: 5 to py: { xs: 8, md: 12 }
          px: { xs: 2, md: 10 }
        }}>
          <YouTubeVideo
            videoUrl={brandData.promo_video}
            loop={brandData.promo_video_loop}
            hideControls={brandData.promo_video_hide_controls}
          />
        </Box>
      )}

      {/* Explore Collections Header + Image Cards */}
      {brandAssets.length > 0 && (
        <Box sx={{
          // Add conditional top padding when YouTube video is not present
          pt: brandData.promo_video ? 0 : { xs: 8, md: 12 }
        }}>
          {/* Section Header */}
          <Box sx={{
            textAlign: 'center',
            mb: { xs: 4, md: 6 },
            px: { xs: 2, md: 4 }
          }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: '500',
                color: '#000',
                fontSize: { xs: '36px', md: '56px' },
                lineHeight: { xs: '40px', md: '60px' },
                mb: 2
              }}
            >
              Explore Our International Brands
            </Typography>

          </Box>

          {/* Image Cards - API brands only */}
          <ImageCards
            images={brandAssets}
            hasBackgroundText={true}
            useInternalLinks={true}
          />
        </Box>
      )}
    </>
  );
};

export default InternationalBrandPage;